// The demo "From Allen" notifications — the single source shared by the notification
// center (nav bell → From Allen tab) and Today's "Allen also flagged" preview, so the
// preview and the center are always the same items (preview == center). In production
// both come from GET /coach/notifications; here they're static, matching the demo
// store scenario. Shape mirrors CoachNotification.to_dict + the AllenNoticed feed
// ({id, severity, title, detail, when} for the card; kind + deepLink for routing).
// Copy mirrors what coach/findings.py produces.

// The verdict's € is CALCULATED from the demo Actions tab's own tracked action (same
// module as its "/ month" and the home's "Delivered by Allen"), never retyped.
import { buildDemoTracked, computeImpact } from '@/demo/guidanceDemo.js'
import { withNl } from '@/demo/demoLang'
import { formatEuro } from '@/i18n/format'
const _pt = buildDemoTracked().find((a) => a.scope === 'Power Tools')
const _ptRounded = Math.round((_pt ? computeImpact(_pt).eur : 0) / 10) * 10
const _ptEur = `+€${_ptRounded.toLocaleString('en-US')}/mo`
// The same figure in Dutch: "+€ 1.230 per maand".
const _ptEurNl = `+${formatEuro(_ptRounded, { lang: 'nl' })} per maand`

// Forecast figures follow from the demo's own daily averages (demo_fixed_widgets.json:
// store 472/day over 24 open days; Power Tools 5,100 → 212/day; Safety Shoes 790 →
// 33/day) × the stated trend: 472×1.12 ≈ 530, 212×1.11 ≈ 235, 33×0.82 ≈ 27.
// Each item carries its Dutch (withNl, demo/demoLang.js) with the same figures.
export const DEMO_ALLEN_NOTIFICATIONS = [
  withNl({
    // A guidance VERDICT (phase 2): a tracked change that proved out. Matches the
    // demo's Power Tools success + the "Value delivered" run-rate.
    id: 'n-powertools-verdict',
    kind: 'guidance_verdict',
    severity: 'good',
    title: `Power Tools: ${_ptEur}, measured`,
    detail: `Your demo-unit change has proven out — ${_ptEur} measured over the weeks since, versus before.`,
    when: 'measured, 4+ weeks in',
    deepLink: '/storalytic_demo_store/store_level?tab=actions-layer&zone=Power%20Tools',
  }, {
    title: `Power Tools: ${_ptEurNl}, gemeten`,
    detail: `Je demotoestel heeft zich bewezen: ${_ptEurNl} gemeten in de weken erna, vergeleken met ervoor.`,
    when: 'gemeten, na 4+ weken',
  }),
  withNl({
    // The WEEKLY suggestions nudge (coach/suggestion_digest.py): suggestions are never
    // pushed one by one; once a week Allen says how many are new on the Actions tab.
    // Both are open suggestions in the demo Actions tab.
    id: 'n-new-suggestions',
    kind: 'new_suggestions',
    severity: 'warning',
    title: 'Allen has 2 new suggestions for you',
    detail: 'New this week: Paint & Decor and Garden Utilities. They\'re waiting on the Actions tab — implement one, keep it in consideration, or tell Allen it\'s not for you.',
    when: 'this week',
    // Structured list of the new suggestions' zones — Today v2 tags those focus cards
    // "New" instead of repeating this item as a flag. (The backend finding carries
    // `zone` only when there is exactly one; v2 in production would need `zones`.)
    zones: ['Paint & Decor', 'Garden Utilities'],
    deepLink: '/storalytic_demo_store/store_level?tab=actions-layer',
  }, {
    title: 'Allen heeft 2 nieuwe voorstellen voor je',
    detail: 'Nieuw deze week: Paint & Decor en Garden Utilities. Ze staan klaar in de tab Acties — voer er een uit, houd het in overweging of laat Allen weten dat het niet bij je past.',
    when: 'deze week',
  }),
  withNl({
    // Weather heads-up — a forward-looking, weather-accented flag. Pops out of the
    // feed (sky rail + cloud glyph) precisely when weather is worth raising, without
    // being a warning. This is weather's home on the landing: an occasional flag,
    // not a permanent widget.
    id: 'n-weather-ahead',
    kind: 'weather_outlook',
    severity: 'info',
    accent: 'weather',
    icon: 'bi-cloud-sun-fill',
    title: 'Warm, dry weekend ahead — expect it a little busier',
    detail: 'Newell DIY runs a touch busier in warm, dry weather, and Garden Utilities leads it — traffic there tends to build 1–2 days before a dry spell. Worth staffing Garden a little heavier.',
    when: 'next 3 days · forecast',
    deepLink: '/storalytic_demo_store/store_level',
  }, {
    title: 'Warm, droog weekend op komst — reken op iets meer drukte',
    detail: 'Newell DIY is net iets drukker bij warm, droog weer, en Garden Utilities loopt voorop: de drukte daar bouwt zich meestal 1–2 dagen vóór een droge periode op. Zet in Garden best wat meer medewerkers in.',
    when: 'komende 3 dagen · verwachting',
  }),
  withNl({
    // Holiday heads-up (coach/findings.py::_calendar_outlook_finding) — only when the
    // store's own data proves an effect for this holiday type. Info: Home + mail, no bell.
    id: 'n-holiday-ahead',
    kind: 'calendar_outlook',
    severity: 'info',
    icon: 'bi-calendar-event',
    title: 'Autumn Holidays starts Saturday',
    detail: 'During short school breaks this store\'s traffic is usually about 15% higher than on a comparable normal day.',
    when: 'Sat 24 Oct – Sun 1 Nov · school holiday',
    deepLink: '/storalytic_demo_store/store_level',
  }, {
    title: 'Herfstvakantie begint zaterdag',
    detail: 'Tijdens korte schoolvakanties is het in deze winkel meestal zo\'n 15% drukker dan op een vergelijkbare gewone dag.',
    when: 'za 24 okt – zo 1 nov · schoolvakantie',
  }),
  withNl({
    id: 'n-footfall-up',
    kind: 'store_outlook',
    severity: 'info',
    title: 'Store footfall — more visitors lately',
    detail: 'Store visits are up about 12% versus the previous 4 weeks. Allen predicts ~530 visitors/day next week.',
    when: 'recent-weeks trend + 7-day forecast',
    deepLink: '/storalytic_demo_store/store_level?tab=data-foundation-layer',
  }, {
    title: 'Winkelbezoekers — meer bezoekers de laatste tijd',
    detail: 'Het aantal winkelbezoekers ligt zo\'n 12% hoger dan in de vorige 4 weken. Allen verwacht volgende week ~530 bezoekers per dag.',
    when: 'trend van de laatste weken + verwachting voor 7 dagen',
  }),
  withNl({
    id: 'n-checkout-wait',
    kind: 'checkout_change',
    severity: 'warning',
    title: 'Longer checkout waits — Lane 2',
    detail: 'The average wait there is up about 45% — roughly 1m10s now, was about 48s.',
    when: 'this month vs last month',
    deepLink: '/storalytic_demo_store/store_level?tab=data-foundation-layer&sub=sec-checkout-waiting',
  }, {
    title: 'Langere wachttijd aan de kassa — Lane 2',
    detail: 'De gemiddelde wachttijd daar is zo\'n 45% gestegen: nu ongeveer 1 min 10 sec, eerder ongeveer 48 sec.',
    when: 'deze maand t.o.v. vorige maand',
  }),
  withNl({
    // Counter unattended (docs §28.6, project_counter_unattended_measurement). Fires on
    // customers who reached a counter past its OWN "too long" bar — the TriggerActionThreshold
    // the shop already set for the live alert — NOT a month-over-month shift. It is a WARNING
    // (push + its own mail) only when the breaches RECUR at a nameable time (structural, fixable
    // — so we name the window, "Mostly …"); scattered breaches are info. A zone with no bar set
    // raises nothing; only the raw numbers show in Explore. Mirrors
    // coach/findings.py::_counter_long_wait_findings. This demo shows the structural (warning) case.
    id: 'n-counter-unattended',
    kind: 'counter_long_wait',
    severity: 'warning',
    title: 'Customers reaching an unmanned counter — Customer Service Desk',
    detail: 'Over the last 30 days, 8 customers reached an unmanned counter past your 1 min '
          + 'limit: 8 at Customer Service Desk. Longest 4 min 10 sec. Mostly in the afternoons.',
    when: 'last 30 days',
    deepLink: '/storalytic_demo_store/store_level?tab=data-foundation-layer&sub=sec-service',
  }, {
    title: 'Klanten aan een onbemande balie — Customer Service Desk',
    detail: 'In de laatste 30 dagen stonden 8 klanten langer dan je grens van 1 min aan een onbemande balie: '
          + '8 bij Customer Service Desk. Langste 4 min 10 sec. Vooral \'s middags.',
    when: 'laatste 30 dagen',
  }),
  withNl({
    id: 'n-safety-down',
    kind: 'zone_outlook',
    severity: 'info',
    title: 'Safety Shoes — fewer visitors lately',
    detail: 'Visits are down about 18% versus the previous weeks — few shoppers are reaching it. Allen predicts ~27 visitors/day next week.',
    when: 'recent-weeks trend + 7-day forecast',
    deepLink: '/storalytic_demo_store/store_level?tab=intelligence-layer&zone=Safety%20Shoes',
  }, {
    title: 'Safety Shoes — minder bezoekers de laatste tijd',
    detail: 'Het aantal bezoeken ligt zo\'n 18% lager dan in de vorige weken: weinig bezoekers vinden de weg ernaartoe. Allen verwacht volgende week ~27 bezoekers per dag.',
    when: 'trend van de laatste weken + verwachting voor 7 dagen',
  }),
  withNl({
    id: 'n-power-up',
    kind: 'zone_outlook',
    severity: 'info',
    title: 'Power Tools — more visitors lately',
    detail: 'Visits are up about 11% versus the previous weeks. Allen predicts ~235 visitors/day next week.',
    when: 'recent-weeks trend + 7-day forecast',
    deepLink: '/storalytic_demo_store/store_level?tab=intelligence-layer&zone=Power%20Tools',
  }, {
    title: 'Power Tools — meer bezoekers de laatste tijd',
    detail: 'Het aantal bezoeken ligt zo\'n 11% hoger dan in de vorige weken. Allen verwacht volgende week ~235 bezoekers per dag.',
    when: 'trend van de laatste weken + verwachting voor 7 dagen',
  }),
]
